<script setup>
/**
 * AppDataTable — Vuetify 3 data table wrapper with DsDataTable-compatible API.
 * Supports column definitions, row-key, search, loading, empty text, and
 * per-column cell slots (#cell-{key}).
 */
import { ref, computed } from "vue";

const props = defineProps({
  columns:           { type: Array,   default: () => [] },
  rows:              { type: Array,   default: () => [] },
  loading:           { type: Boolean, default: false },
  rowKey:            { type: String,  default: "id" },
  searchable:        { type: Boolean, default: false },
  searchPlaceholder: { type: String,  default: "Search…" },
  emptyText:         { type: String,  default: "No records found." },
  caption:           { type: String,  default: "" },
});

const search = ref("");

const headers = computed(() =>
  props.columns.map((c) => ({
    title:    c.label,
    key:      c.key,
    sortable: c.sortable ?? false,
    align:    c.align === "end" ? "end" : "start",
    width:    c.width,
  }))
);
</script>

<template>
  <VCard>
    <VCardText v-if="searchable">
      <VTextField
        v-model="search"
        :placeholder="searchPlaceholder"
        prepend-inner-icon="mdi:magnify"
        clearable
        hide-details
        density="compact"
        style="max-width: 360px"
      />
    </VCardText>

    <VDataTable
      :headers="headers"
      :items="rows"
      :loading="loading"
      :search="search"
      :item-value="rowKey"
      :no-data-text="emptyText"
      :caption="caption"
      hover
    >
      <template v-for="col in columns" #[`item.${col.key}`]="{ item }">
        <div :style="col.align !== 'end' ? 'max-width:250px; word-break:break-word; white-space:normal' : undefined">
          <slot :name="`cell-${col.key}`" :row="item">
            {{ item[col.key] }}
          </slot>
        </div>
      </template>
    </VDataTable>
  </VCard>
</template>

<style scoped>
:deep(.v-table__wrapper) {
  overflow-x: hidden;
}
</style>

